/**
 * BoothVideoMessage — "Tell the Night Something" inside Booth OS.
 *
 * 30-second video record with calm intro, live recording with hairline
 * gold progress, review, and preserve. Uploads via the same booth
 * photo endpoint with a video MIME so it flows into the gallery /
 * recap pipeline alongside still captures.
 *
 * Language contract (Mode 1, no SaaS):
 *   "Tell the night something ✦"
 *   "Your words have been preserved ✦"
 *   never "uploading" / "processing" / "sending".
 */
import React, { useState, useEffect, useRef } from 'react';
import { SERIF, Eyebrow, Heading, Lede, GoldPill, FlowFrame } from './primitives';
import { sendOrQueue, newDedupeId } from '../../lib/booth-os/uploadQueue';
import { useBoothCameraPipeline } from '../../hooks/useBoothCameraPipeline';
import { useBlobUrlLifecycle } from '../../hooks/useMediaStreamCleanup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_SECONDS = 30;

const BoothVideoMessage = ({ theme, event, onBack, onComplete }) => {
  // 'intro' | 'recording' | 'review' | 'saving'
  const [stage, setStage] = useState('intro');
  const [elapsed, setElapsed] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);

  const reviewRef = useRef(null);
  const tickRef = useRef(null);

  // iter211d ✦ — Booth Camera Pipeline. We use the `guestbook_booth`
  // profile (not `guestbook`) because the booth-shell consumer
  // records the camera stream directly and needs the mic acquired
  // inside getUserMedia. The standalone `GuestbookRecorder` page
  // uses `guestbook` because it layers `useParticipationAudioPipeline`
  // on top — opening the mic twice on iOS causes track exclusivity
  // failures, so the two consumers each have a profile.
  const cam = useBoothCameraPipeline({ profile: 'guestbook_booth' });
  const { videoRef, facingMode, error, previewStyle, recordingMime, FramingGuide } = cam;

  // Render-phase ref so the cleanup effect can read the LATEST
  // stage when deciding whether to stop the camera.
  const stageRef = useRef(stage);
  stageRef.current = stage;

  useEffect(() => {
    // Acquire the camera only if it isn't already live.
    if (stage === 'intro' || stage === 'recording') {
      if (!cam.streamRef.current) cam.start();
    }
    return () => {
      const newStage = stageRef.current;
      if (newStage !== 'intro' && newStage !== 'recording') cam.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // iter209a ✦ — Unmount cleanup with stable-identity dep.
  //
  // The original `[cam]` dep made this cleanup re-fire on every
  // parent render (the pipeline returns a fresh object literal each
  // render → `cam` reference-unequal every render). That cleanup
  // called `cam.stop()` immediately after every successful
  // `cam.start()`, killing the Android Chrome stream before the
  // video element could paint a frame. Result: black preview on
  // the "Speak softly ✦" surface.
  //
  // Fix: depend on the actual stable function refs (cam.start /
  // cam.stop). The hook holds them stable as long as their own
  // deps don't change (`profile` is frozen here, `facingMode` only
  // flips on user gesture). The unmount cleanup now fires once.
  useEffect(() => () => { cam.stop(); if (tickRef.current) clearInterval(tickRef.current); }, [cam.stop]);

  // Auto-revoke the recording's object URL whenever it changes or
  // the component unmounts.
  useBlobUrlLifecycle(blobUrl);

  const startRecording = () => {
    if (!cam.streamRef.current) return;
    const ok = cam.startRecording();
    if (!ok) return;
    setElapsed(0);
    setStage('recording');
    tickRef.current = setInterval(() => {
      setElapsed((s) => {
        if (s + 1 >= MAX_SECONDS) {
          stopRecording();
          return MAX_SECONDS;
        }
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = async () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    const { blob, mime } = await cam.stopRecording();
    if (!blob || !blob.size) {
      // Empty recording — likely mic/camera permission was denied
      // silently or the stream produced no frames. Don't advance
      // to a broken review surface.
      setStage('intro');
      return;
    }
    setRecordedBlob(blob);
    setBlobUrl(URL.createObjectURL(blob));
    setStage('review');
    // recordingMime is exposed by the hook; we just touch it so the
    // linter is happy and the consumer can label downloads honestly.
    void mime;
  };

  const flip = () => { cam.switchCamera(); };

  const retake = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null); setRecordedBlob(null);
    setElapsed(0); setStage('intro');
  };

  const preserve = async () => {
    if (!recordedBlob) return;
    setStage('saving');
    try {
      // Use the pipeline's negotiated mime to derive an honest
      // extension — iOS Safari fallback to mp4 lands as .mp4,
      // chrome desktop stays .webm.
      const ext = (recordingMime || recordedBlob.type || '').includes('mp4') ? 'mp4' : 'webm';
      const fd = new FormData();
      fd.append('file', recordedBlob, `booth-message-${Date.now()}.${ext}`);
      const id = newDedupeId();
      fd.append('dedupe_id', id);
      const r = await sendOrQueue({
        kind: 'multipart',
        url: `${API}/events/${event.event_id}/photos/booth-guest?guest_id=booth_os_message&dedupe_id=${id}`,
        formData: fd,
        dedupeId: id,
      });
      setTimeout(() => onComplete({
        deferred: !r.viaNetwork,
        // iter210 ✦ — Personal Memory Return Path. The dedupe_id
        // serves as the contribution_id so the Personal Memory
        // Page can resolve this voice message.
        contribution_id: id,
      }), 1100);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[BoothVideoMessage] preserve failed', e?.message);
      setStage('review');
    }
  };

  return (
    <FlowFrame theme={theme} eyebrow="✦ TELL THE NIGHT SOMETHING" onBack={onBack} testId="booth-video-message">
      {stage === 'intro' && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 24,
        }}>
          <Heading theme={theme}>Speak softly ✦</Heading>
          <Lede theme={theme}>
            Up to thirty seconds. A line for the morning, a memory for the room,
            a thank-you to whoever made tonight possible.
          </Lede>
          <CameraFrame videoRef={videoRef} theme={theme} previewStyle={previewStyle} error={error} FramingGuide={FramingGuide} />
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <GoldPill theme={theme} large onClick={startRecording} testId="booth-message-record">
              Begin the message ✦
            </GoldPill>
            <GoldPill theme={theme} secondary onClick={flip} testId="booth-message-flip">
              Flip camera
            </GoldPill>
          </div>
        </div>
      )}

      {stage === 'recording' && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 24,
        }}>
          <Eyebrow theme={theme}>✦ THE NIGHT IS LISTENING</Eyebrow>
          <CameraFrame
            videoRef={videoRef} theme={theme} previewStyle={previewStyle} error={error} FramingGuide={FramingGuide}
            overlay={
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                padding: 24, pointerEvents: 'none',
              }}>
                <div style={{
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 18, color: theme.gold,
                  letterSpacing: '0.16em',
                  background: 'rgba(0,0,0,0.32)', padding: '6px 14px',
                  borderRadius: 999, backdropFilter: 'blur(4px)',
                }}>
                  {Math.max(0, MAX_SECONDS - elapsed)}s remaining
                </div>
              </div>
            }
          />
          {/* Hairline gold progress */}
          <div style={{
            width: 'min(620px, 90vw)', height: 2,
            background: 'rgba(212,170,82,0.18)', borderRadius: 2,
          }}>
            <div
              data-testid="booth-message-progress"
              style={{
                width: `${(elapsed / MAX_SECONDS) * 100}%`,
                height: 2,
                background: theme.gold,
                transition: 'width 900ms linear',
              }}
            />
          </div>
          <GoldPill theme={theme} onClick={stopRecording} testId="booth-message-stop">
            Settle the message ✦
          </GoldPill>
        </div>
      )}

      {stage === 'review' && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 22,
        }}>
          <Eyebrow theme={theme}>✦ HOLD THE MESSAGE</Eyebrow>
          <Heading theme={theme}>Listen back ✦</Heading>
          <video
            ref={reviewRef}
            data-testid="booth-message-review"
            src={blobUrl}
            controls
            playsInline
            style={{
              width: 'min(620px, 90vw)', aspectRatio: '4 / 3',
              borderRadius: 14, background: '#000',
              border: `1px solid ${theme.hair}`,
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}
          />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <GoldPill theme={theme} large onClick={preserve} testId="booth-message-preserve">
              Preserve the message ✦
            </GoldPill>
            <GoldPill theme={theme} secondary onClick={retake} testId="booth-message-retake">
              Speak again
            </GoldPill>
          </div>
        </div>
      )}

      {stage === 'saving' && (
        <div data-testid="booth-message-saving" style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          <Eyebrow theme={theme}>✦ PRESERVING</Eyebrow>
          <Heading theme={theme}>The message is settling in ✦</Heading>
        </div>
      )}
    </FlowFrame>
  );
};

const CameraFrame = ({ videoRef, theme, previewStyle, error, overlay, FramingGuide }) => {
  const frameRef = useRef(null);
  return (
    <div ref={frameRef} style={{
      width: 'min(620px, 90vw)', aspectRatio: '4 / 3',
      borderRadius: 14, overflow: 'hidden',
      background: '#000', border: `1px solid ${theme.hair}`,
      boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      position: 'relative',
    }}>
      <video
        ref={videoRef} playsInline muted
        style={previewStyle}
      />
      {FramingGuide && <FramingGuide longPressTarget={frameRef} />}
      {overlay}
      {error && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: theme.ink, fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 15, textAlign: 'center', padding: 32,
        }}>{error}</div>
      )}
    </div>
  );
};

export default BoothVideoMessage;
